import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Event,
  MixpanelEventV2,
  sendMixpanelEvent,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import { Org } from 'types/organization';

import {
  LogoutModal,
  SwitchAccountModal,
  UpgradeToOrgModal
} from './components';

interface AccountModalsProps {
  orgList?: Org[];
  setAccountMethods: (methods: any) => void;
}

const AccountModals = (props: AccountModalsProps) => {
  const { orgList, setAccountMethods } = props;

  const { currentUser, currentOrg, rolesAndPermissions } = useSelector(
    (state: any) => state.user
  );

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);
  const [isUpgradeToOrgModalOpen, setUpgradeToOrgModalOpen] =
    useState<boolean>(false);
  const [isSwitchAccountModalOpen, setSwitchAccountModalOpen] =
    useState<boolean>(false);

  const onClickLogout = () => {
    setIsLogoutModalOpen(true);
    sendMixpanelEvent(Event.LOGOUT_CLICKED);
    sendMixpanelEventV2(MixpanelEventV2.LOG_OUT_CLICKED);
  };

  const onCancelLogout = () => {
    setIsLogoutModalOpen(false);
    sendMixpanelEvent(Event.LOGOUT_CANCEL_BUTTON_CLICKED);
    sendMixpanelEventV2(MixpanelEventV2.LOG_OUT_CANCELLED);
  };

  const onClickUpgradeToOrg = () => {
    setUpgradeToOrgModalOpen(true);
    sendMixpanelEvent(Event.UPGRADE_TO_ORGANIZATION_POPUP_VIEWED, {
      individual_organization_name: currentUser?.name || ''
    });
    sendMixpanelEventV2(MixpanelEventV2.UPGRADE_TO_ORG_CLICKED, {
      mobile_number: currentUser?.mobile || '',
      user_name: currentUser?.name || ''
    });
  };

  const onCancelUpgradeToOrg = () => {
    setUpgradeToOrgModalOpen(false);
    sendMixpanelEvent(Event.UPGRADE_TO_ORGANIZATION_CANCEL_BUTTON_CLICKED, {
      individual_organization_name: currentUser?.name || ''
    });
    sendMixpanelEventV2(MixpanelEventV2.UPGRADE_TO_ORG_CANCELLED, {
      mobile_number: currentUser?.mobile || '',
      user_name: currentUser?.name || ''
    });
  };

  const onClickSwitchAccount = () => {
    setSwitchAccountModalOpen(true);
    sendMixpanelEvent(Event.SWITCH_ACCOUNT_CLICKED);
    sendMixpanelEventV2(MixpanelEventV2.PROFILE_SWITCH_ACCOUNT_CLICKED);
  };

  const onCancelSwitchAccount = () => {
    setSwitchAccountModalOpen(false);
    sendMixpanelEvent(Event.SWITCH_ACCOUNT_CANCEL_BUTTON_CLICKED);
    sendMixpanelEventV2(MixpanelEventV2.ACCOUNT_SELECTION_CANCELLED, {
      flow: 'manual_switch',
      current_account_name: currentOrg?.name,
      current_user_role:
        rolesAndPermissions?.role_name || currentOrg?.role_name,
      current_account_type: currentOrg?.type
    });
  };

  const closeSwitchAccountModal = () => {
    setSwitchAccountModalOpen(false);
  };

  useEffect(() => {
    setAccountMethods({
      onClickLogout,
      onClickUpgradeToOrg,
      onClickSwitchAccount
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAccountMethods]);

  return (
    <>
      {isUpgradeToOrgModalOpen && (
        <UpgradeToOrgModal
          isOpen={isUpgradeToOrgModalOpen}
          onCancelUpgradeToOrg={onCancelUpgradeToOrg}
          orgList={orgList || []}
          setUpgradeToOrgModalOpen={setUpgradeToOrgModalOpen}
        />
      )}
      {isLogoutModalOpen && (
        <LogoutModal
          isOpen={isLogoutModalOpen}
          onCancelLogout={onCancelLogout}
        />
      )}
      {isSwitchAccountModalOpen && (
        <SwitchAccountModal
          showSwitchAccountModal={isSwitchAccountModalOpen}
          onCancelModal={onCancelSwitchAccount}
          onCloseAfterSwitch={closeSwitchAccountModal}
          orgList={orgList}
        />
      )}
    </>
  );
};

export default AccountModals;
